import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleList } from 'app/core/enums/role-list.enum';
import { MultiLanguageService } from './multi-language.service';

/** Language codes available to filmmaker users. */
const FILMMAKER_ALLOWED_LANGUAGES: string[] = ['en', 'ml'];

interface Language {
    _id: string;
    name: string;
    updatedAt: string;
    createdAt: string;
    isDeleted: boolean;
    languageUrl: string;
    appEnabled: boolean;
    default: string;
}

const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
    en: 'English',
    ml: 'മലയാളം',
    ta: 'தமிழ்',
    kn: 'ಕನ್ನಡ',
    hi: 'हिन्दी',
    te: 'తెలుగు',
};

@Component({
    selector: 'app-language-selector',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        FormsModule,
        MatMenuModule,
        TranslatePipe,
    ],
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.scss',
})
export class LanguageSelectorComponent implements OnInit {
    @ViewChild('dropdownRef') dropdownRef!: ElementRef<HTMLSelectElement>;
    @Input() page: string;
    constructor(
        private readonly authService: AuthService,
        public router: Router,
        private _multiLanguageService: MultiLanguageService,
        private _cdr: ChangeDetectorRef
    ) {}
    languagesArray: Language[] = [];
    isDropdownOpen: boolean = false;

    selectedLanguage: string = 'en';

    ngOnInit(): void {
        this._multiLanguageService.currentLanguage$.subscribe((lang) => {
            if (lang) {
                this.selectedLanguage = lang;
                this._cdr.markForCheck();
            }
        });
        this._multiLanguageService.getAppEnabledLanguages().subscribe({
            next: (response) => {
                const allLanguages = (response.data ?? []) as Language[];
                const isFilmMaker =
                    this.authService.currentUserRole === RoleList.FilmMaker;
                this.languagesArray = isFilmMaker
                    ? allLanguages.filter((lang) =>
                          FILMMAKER_ALLOWED_LANGUAGES.includes(lang.default ?? '')
                      )
                    : allLanguages;
                this._cdr.markForCheck();
            },
            error: (err) => {
                console.error(err);
            },
        });
    }

    getNativeName(langCode: string): string {
        return NATIVE_LANGUAGE_NAMES[langCode] || '';
    }

    onLanguageChange(lang: Language): void {
        this._multiLanguageService.setLanguage(lang.default);
        this._multiLanguageService
            .setDefaultLanguage({
                defaultLanguage: lang?.default,
                defaultLanguageId: lang?._id,
            })
            .subscribe({
                next: (res) => {
                    this._multiLanguageService.getNewUserToken().subscribe({
                        next: (res) => {
                            this.authService._saveToken(res.token);
                            if (res.data) {
                                this.authService._saveUser(res.data as unknown as import('app/core/models/filmmaker-user.model').User);
                                this._multiLanguageService.setLanguage(
                                    res.data?.defaultLanguage
                                );
                            }
                            this.router.navigateByUrl(this.router.url);
                        },
                        error: (err) => {
                            console.error('error', err);
                        },
                    });
                },
                error: (err) => {
                    console.error('error', err);
                },
            });
    }
}
